import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Workflow step: transform `rexel_onhnunsh` raw rows into
 * `im_inventory_snapshots` domain rows.
 *
 * Stage 2 of the ONHNUNSH ingest pipeline. Runs after `master_data_bulk_insert`
 * has populated `rexel_onhnunsh`. For each row in the latest batch:
 *   - Resolves `giloc → im_warehouses.id` (auto-creates skeleton warehouses when missing)
 *   - Resolves `giitem → im_products_v2.id` (products must exist — INITINFC is
 *     the source of truth; unknown SKUs are skipped and reported as errors)
 *   - Upserts into `im_inventory_snapshots` keyed on
 *     `(warehouseId, productId, productVariantId, snapshotDate)`
 *
 * Scoping: `updatedAfter` (typically wired from upstream `startedAt`) limits
 * the rexel_onhnunsh rows considered to those touched by the current run.
 */
export const transformOnhnunshSnapshotsStep = defineStepConfig({
  type: "transform_onhnunsh_snapshots",
  name: "Transform ONHNUNSH → Inventory Snapshots",
  description:
    "Reads rexel_onhnunsh rows updated by the current run, FK-resolves giloc/giitem against im_warehouses/im_products_v2 (auto-creates missing warehouses; skips unknown products with error), and upserts into im_inventory_snapshots.",
  category: "Transformation",
  libraryModule: "Procurement",
  inputSchema: z.object({
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "ISO timestamp — only process rexel_onhnunsh rows updated after this time. " +
          "Typically wired from the upstream master_data_bulk_insert's startedAt output.",
      ),
  }),
  outputSchema: z.object({
    snapshotsUpserted: z
      .number()
      .describe("Number of im_inventory_snapshots rows upserted"),
    autoCreatedWarehouses: z
      .number()
      .describe(
        "Number of im_warehouses rows auto-created because they didn't exist for the seen GILOC values",
      ),
    skippedProductCount: z
      .number()
      .describe(
        "Number of distinct product SKUs skipped because they don't exist in im_products_v2 (run INITINFC ingest first)",
      ),
    skippedWarehouseCount: z
      .number()
      .describe(
        "Number of distinct warehouse codes skipped (unresolvable division for auto-create)",
      ),
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "Pass-through of the input timestamp so downstream steps can scope to the current run.",
      ),
  }),
  configSchema: z.object({
    batchSize: z
      .number()
      .min(1)
      .max(5000)
      .prefault(500)
      .describe("Number of im_inventory_snapshots rows to upsert per batch"),
    autoCreateMissing: z
      .boolean()
      .prefault(true)
      .describe(
        "If true, missing warehouses are auto-created as skeletons. " +
          "Products are never auto-created — INITINFC is the source of truth.",
      ),
  }),
  emits: [] as const,
  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
